/**
 * Generates a Home Assistant package that sends controller data to ReefTracker
 * once per window of `intervalHours` (1–24 h, #1899):
 *  - per probe the current value (the probe's last reading, as the controller
 *    shows it) plus two `statistics` sensors for the minimum / maximum of the
 *    window. The value is not an average: probes that measure only every few
 *    hours (e.g. a KH Director) would let one outlier skew it for a whole
 *    window. `sampling_size` is large because the statistics buffer is capped
 *    by sample count as well as age, and keep_last_sample keeps a value that
 *    has not changed for the window;
 *  - for the total power an `integration` sensor (Riemann sum, kWh): a meter
 *    that stays exact however rarely it is sent;
 *  - a rest_command with the device token from secrets.yaml and an automation
 *    that posts one batch at the end of each window (e.g. 00:00, 08:00, 16:00
 *    for 8 h). The automation renders the readings list; the rest_command wraps
 *    it as {"origin": …, "readings": […]} — origin labels the device (e.g. GHL ProfiLux).
 * Needs Home Assistant 2024.10 or newer (`triggers:` / `actions:` syntax).
 */

export interface HomeAssistantConfigInput {
  /** Absolute ingest URL of the source. */
  endpointUrl: string;
  /** Probe entity ids (sensor.…), sent as current value with the min / max of each window. */
  probeEntities: readonly string[];
  /** Total power entity (W), sent as a kWh meter; null for none. */
  powerEntity: string | null;
  /** Window and push cycle in hours (one of PUSH_INTERVALS). */
  intervalHours: number;
  /** Device label sent with every batch (e.g. "GHL ProfiLux"); empty for none. */
  origin: string;
}

/** Push cycles offered: they divide a day, so the windows line up with the clock (time_pattern hours: /N). */
export const PUSH_INTERVALS = [1, 2, 4, 8, 12, 24] as const;

/** The origin as it is written into the template: no quotes, braces or YAML comment signs. */
export function cleanOrigin(origin: string): string {
  return origin.replace(/[{}%"'\\#\r\n]/g, '').trim().slice(0, 100);
}

/** Home Assistant entity ids: domain.object_id, lower case, digits and underscores. */
const ENTITY_ID = /^[a-z_][a-z0-9_]*\.[a-z0-9_]+$/;

export function isEntityId(value: string): boolean {
  return ENTITY_ID.test(value);
}

/** Entity ids from a textarea (one per line, or separated by commas / spaces), valid and de-duplicated. */
export function parseEntityList(text: string): { valid: string[]; invalid: string[] } {
  const items = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const item of items) {
    if (!isEntityId(item)) invalid.push(item);
    else if (!valid.includes(item)) valid.push(item);
  }
  return { valid, invalid };
}

/** Object id of the helper entities this package creates for an entity. */
function helperBase(entityId: string): string {
  return `reeftracker_${entityId.split('.')[1] ?? entityId}`;
}

/** Key the energy meter of the power entity is reported as. */
export function energyKey(powerEntity: string): string {
  return `sensor.${helperBase(powerEntity)}_energy`;
}

/** The power entity an energy-meter key of this package was made from (inverse of energyKey); null for other keys. */
export function powerEntityOfEnergyKey(key: string): string | null {
  const match = /^sensor\.reeftracker_([a-z0-9_]+)_energy$/.exec(key);
  return match ? `sensor.${match[1]}` : null;
}

function statisticsSensor(entityId: string, suffix: string, characteristic: string, hours: number): string {
  return [
    `  - platform: statistics`,
    `    name: ${helperBase(entityId)}_${suffix}`,
    `    entity_id: ${entityId}`,
    `    state_characteristic: ${characteristic}`,
    `    sampling_size: 5000`,
    `    keep_last_sample: true`,
    `    max_age:`,
    `      hours: ${hours}`,
  ].join('\n');
}

export function buildHomeAssistantConfig({
  endpointUrl,
  probeEntities,
  powerEntity,
  intervalHours,
  origin,
}: HomeAssistantConfigInput): string {
  const hours = (PUSH_INTERVALS as readonly number[]).includes(intervalHours) ? intervalHours : 8;
  const device = cleanOrigin(origin);
  const sensors: string[] = [];
  for (const entity of probeEntities) {
    sensors.push(statisticsSensor(entity, 'min', 'value_min', hours));
    sensors.push(statisticsSensor(entity, 'max', 'value_max', hours));
  }
  if (powerEntity) {
    sensors.push(
      [
        `  - platform: integration`,
        `    name: ${helperBase(powerEntity)}_energy`,
        `    source: ${powerEntity}`,
        `    unit_prefix: k`,
        `    round: 3`,
        `    method: left`,
      ].join('\n'),
    );
  }

  const probeList = probeEntities.map((e) => `'${e}'`).join(', ');
  const payload = [
    `            {% set end = now() %}`,
    `            {% set start = end - timedelta(hours=${hours}) %}`,
    `            {% set ns = namespace(items=[]) %}`,
    `            {% for e in [${probeList}] %}`,
    `              {% set base = 'sensor.reeftracker_' ~ e.split('.')[1] %}`,
    `              {% set low = states(base ~ '_min') %}`,
    `              {% set high = states(base ~ '_max') %}`,
    `              {% if states(e) | is_number and low | is_number and high | is_number %}`,
    `                {% set ns.items = ns.items + [{'key': e, 'value': states(e) | float, 'min': low | float, 'max': high | float, 'unit': state_attr(e, 'unit_of_measurement'), 'measuredAt': end.isoformat(), 'windowStart': start.isoformat()}] %}`,
    // No sample in the window statistics yet (just after a restart): send the
    // current value without a window instead of skipping the probe.
    `              {% elif states(e) | is_number %}`,
    `                {% set ns.items = ns.items + [{'key': e, 'value': states(e) | float, 'unit': state_attr(e, 'unit_of_measurement'), 'measuredAt': end.isoformat()}] %}`,
    `              {% endif %}`,
    `            {% endfor %}`,
    ...(powerEntity
      ? [
          `            {% set kwh = states('${energyKey(powerEntity)}') %}`,
          `            {% if kwh | is_number %}`,
          `              {% set ns.items = ns.items + [{'key': '${energyKey(powerEntity)}', 'value': kwh | float, 'unit': 'kWh', 'measuredAt': end.isoformat()}] %}`,
          `            {% endif %}`,
        ]
      : []),
    `            {{ ns.items | to_json }}`,
  ];

  return [
    `# ReefTracker: controller data every ${hours} ${hours === 1 ? 'hour' : 'hours'} (Home Assistant 2024.10 or newer).`,
    `# 1. Put the device token into secrets.yaml:`,
    `#      reeftracker_token: "<device token>"`,
    `# 2. Enable packages in configuration.yaml (once):`,
    `#      homeassistant:`,
    `#        packages: !include_dir_named packages`,
    `# 3. Save this file as packages/reeftracker.yaml and restart Home Assistant.`,
    ``,
    ...(sensors.length > 0 ? [`sensor:`, sensors.join('\n'), ``] : []),
    `rest_command:`,
    `  reeftracker_push:`,
    `    url: "${endpointUrl}"`,
    `    method: POST`,
    `    headers:`,
    `      X-Device-Token: !secret reeftracker_token`,
    `    content_type: "application/json"`,
    // The automation's template may arrive as a list (Home Assistant parses
    // rendered results) or as a string (when that parse fails, e.g. on null).
    `    payload: >`,
    `      {${device ? `"origin": "${device}", ` : ''}"readings": {% if readings is string %}{{ readings }}{% else %}{{ readings | to_json }}{% endif %}}`,
    ``,
    `automation:`,
    // A package automation stays read-only in the UI either way; the id makes
    // Home Assistant keep its traces, so a failed push can be looked up.
    `  - id: reeftracker_send_controller_data`,
    `    alias: "ReefTracker: send controller data"`,
    `    triggers:`,
    `      - trigger: time_pattern`,
    `        hours: "/${hours}"`,
    `        minutes: 0`,
    `    actions:`,
    `      - action: rest_command.reeftracker_push`,
    `        data:`,
    `          readings: >`,
    ...payload,
    ``,
  ].join('\n');
}
